'use client';

import { useState, useEffect } from 'react';
import { useRouter, usePathname } from 'next/navigation';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  Plus,
  Loader2,
  Home,
  Sparkles,
  Crown,
  Star,
  PanelLeftClose,
  PanelLeft,
} from 'lucide-react';
import { UserDomain } from '@/lib/services/domain-switching-service';
import { toast } from 'sonner';
import { CreateDomainModal } from '@/components/settings/CreateDomainModal';
import { MomentoVoice, MOMENTO_VOICES } from '@/components/livekit/VoiceSelectionModal';
import Image from 'next/image';
import { SidebarDomainDropdown } from './SidebarDomainDropdown';
import { SidebarNav } from './SidebarNav';
import { QuickRoomAccess } from './QuickRoomAccess';

export function CompactDomainSwitcher() {
  const router = useRouter();
  const pathname = usePathname();
  const [domains, setDomains] = useState<UserDomain[]>([]);
  const [activeDomain, setActiveDomain] = useState<UserDomain | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSwitching, setIsSwitching] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [domainInfo, setDomainInfo] = useState<{
    plan_type?: string;
    credits?: number;
  } | null>(null);
  const [domainsInfo, setDomainsInfo] = useState<
    Record<string, { plan_type?: string; credits?: number }>
  >({});
  const [isStartingInterview, setIsStartingInterview] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(() => {
    if (typeof window !== 'undefined') {
      const stored = localStorage.getItem('sidebar-collapsed');
      return stored === null ? true : stored === 'true';
    }
    return true;
  });

  useEffect(() => {
    localStorage.setItem('sidebar-collapsed', String(isCollapsed));
  }, [isCollapsed]);

  useEffect(() => {
    fetchDomains();
  }, []);

  useEffect(() => {
    domains.forEach((domain) => {
      fetchDomainInfoForDomain(domain.domain_id);
    });
  }, [domains]);

  useEffect(() => {
    if (activeDomain?.domain_id) {
      fetchDomainInfo(activeDomain.domain_id);
    }
  }, [activeDomain?.domain_id]);

  const fetchDomains = async () => {
    try {
      const response = await fetch('/api/domains/switch');
      if (!response.ok) throw new Error('Failed to fetch domains');

      const data = await response.json();
      setDomains(data.domains || []);
      setActiveDomain(data.activeDomain || null);
    } catch (error) {
      console.error('Error fetching domains:', error);
      toast.error('Failed to load domains');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchDomainInfo = async (domainId: string) => {
    try {
      const response = await fetch(`/api/domains/${domainId}/subscription`);
      if (!response.ok) return;

      const data = await response.json();
      setDomainInfo({
        plan_type: data.subscription?.plan_type || 'free',
        credits: Math.floor(data.credits?.balance || 0),
      });
    } catch (error) {
      console.error('Error fetching domain info:', error);
    }
  };

  const fetchDomainInfoForDomain = async (domainId: string) => {
    try {
      const response = await fetch(`/api/domains/${domainId}/subscription`);
      if (!response.ok) return;

      const data = await response.json();
      setDomainsInfo((prev) => ({
        ...prev,
        [domainId]: {
          plan_type: data.subscription?.plan_type || 'free',
          credits: Math.floor(data.credits?.balance || 0),
        },
      }));
    } catch (error) {
      console.error('Error fetching domain info:', error);
    }
  };

  const switchDomain = async (domainId: string) => {
    if (isSwitching) return;

    const isAlreadyActive = activeDomain?.domain_id === domainId;

    setIsSwitching(true);
    try {
      if (!isAlreadyActive) {
        const response = await fetch('/api/domains/switch', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ domainId }),
        });

        if (!response.ok) throw new Error('Failed to switch domain');

        const data = await response.json();
        setActiveDomain(data.activeDomain);

        toast.success(`Switched to ${data.activeDomain.domain_name}`);
        // Force full page reload to ensure all data refreshes with new domain context
        window.location.href = pathname || '/dashboard/posts';
      } else {
        router.push('/dashboard/posts');
        toast.success('Navigating to Dashboard...');
      }
    } catch (error) {
      console.error('Error switching domain:', error);
      toast.error('Failed to switch domain');
    } finally {
      setIsSwitching(false);
    }
  };

  const handleVoiceSelected = async (voice: MomentoVoice) => {
    if (!activeDomain) return;

    setIsStartingInterview(true);

    try {
      const response = await fetch('/api/agent/interview', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          domainId: activeDomain.domain_id,
          voiceId: voice.id,
          voiceName: voice.name,
        }),
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to start interview');
      }

      const data = await response.json();

      const params = new URLSearchParams({
        token: data.token,
        serverUrl: data.serverUrl,
      });

      router.push(`/rooms/${data.roomName}?${params.toString()}`);
    } catch (error) {
      console.error('[TalkToMomento] Error:', error);
      toast.error(
        error instanceof Error ? error.message : 'Failed to start conversation'
      );
      setIsStartingInterview(false);
    }
  };

  if (isLoading) {
    return (
      <div
        className={cn(
          'flex items-center justify-center h-full',
          isCollapsed ? 'w-16' : 'w-64'
        )}
      >
        <Loader2 className="h-5 w-5 animate-spin" />
      </div>
    );
  }

  return (
    <>
      <TooltipProvider delayDuration={0}>
        <div
          className={cn(
            'flex flex-col h-full transition-all duration-200',
            isCollapsed ? 'w-16' : 'w-64'
          )}
        >
          {/* Sticky Top: Home + Collapse Toggle + Domain Selector */}
          <div className="sticky top-0 z-20 border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/80">
            {/* Home Button + Collapse Toggle Row */}
            <div className={cn('p-2 flex items-center', isCollapsed ? 'justify-center' : 'justify-between px-3')}>
              {isCollapsed ? (
                <div className="flex flex-col items-center gap-1">
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => router.push('/home')}
                        className="h-10 w-10 rounded-lg hover:bg-muted"
                      >
                        <Home className="h-5 w-5" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent side="right">Home</TooltipContent>
                  </Tooltip>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => setIsCollapsed(false)}
                        className="h-8 w-8 rounded-lg hover:bg-muted"
                      >
                        <PanelLeft className="h-4 w-4" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent side="right">Expand sidebar</TooltipContent>
                  </Tooltip>
                </div>
              ) : (
                <>
                  <Button
                    variant="ghost"
                    onClick={() => router.push('/home')}
                    className="justify-start gap-3 h-10 flex-1"
                  >
                    <Home className="h-5 w-5" />
                    <span className="font-medium">Home</span>
                  </Button>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => setIsCollapsed(true)}
                        className="h-8 w-8 rounded-lg hover:bg-muted shrink-0"
                      >
                        <PanelLeftClose className="h-4 w-4" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent side="right">Collapse sidebar</TooltipContent>
                  </Tooltip>
                </>
              )}
            </div>

            {/* Domain Selector */}
            <div className={cn('px-2 pb-3', isCollapsed && 'px-3')}>
              <SidebarDomainDropdown
                activeDomain={activeDomain}
                domains={domains}
                domainsInfo={domainsInfo}
                onSwitch={switchDomain}
                isSwitching={isSwitching}
                isCollapsed={isCollapsed}
                onExpand={() => setIsCollapsed(false)}
              />
            </div>

            {/* Quick Room Access */}
            <div className={cn('px-2 pb-2', isCollapsed && 'px-3')}>
              <QuickRoomAccess
                isCollapsed={isCollapsed}
                activeDomain={activeDomain ? {
                  domain_id: activeDomain.domain_id,
                  domain_handle: activeDomain.domain_handle,
                  role: activeDomain.role,
                } : null}
              />
            </div>
          </div>

          {/* Scrollable Middle: Navigation + Momento */}
          <div className="flex-1 overflow-y-auto p-2 space-y-4">
            {/* Navigation Links */}
            <SidebarNav isCollapsed={isCollapsed} />

            {/* Momento Section */}
            <div className="pt-4 border-t space-y-1">
              {/* Momento Text Chat - Links to /momento */}
              {isCollapsed ? (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      className={cn(
                        'relative h-12 w-12 rounded-lg transition-all p-0 hover:scale-105 mx-auto',
                        pathname?.startsWith('/momento') && 'bg-primary/10'
                      )}
                      onClick={() => router.push('/momento')}
                    >
                      <Image
                        src="/momento-icon.svg"
                        alt="Momento"
                        width={40}
                        height={40}
                        className="w-10 h-10"
                      />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent side="right">
                    <p className="font-medium">Momento</p>
                    <p className="text-xs text-muted-foreground">Find gems in your videos</p>
                  </TooltipContent>
                </Tooltip>
              ) : (
                <Button
                  variant="ghost"
                  onClick={() => router.push('/momento')}
                  className={cn(
                    'w-full justify-start gap-3 h-12',
                    pathname?.startsWith('/momento') && 'bg-primary/10 text-primary hover:bg-primary/15'
                  )}
                >
                  <Image
                    src="/momento-icon.svg"
                    alt="Momento"
                    width={28}
                    height={28}
                    className="w-7 h-7"
                  />
                  <span className="font-medium">Momento</span>
                </Button>
              )}

              {/* Voice Agents - Direct access to Kiefer and Tessa */}
              {MOMENTO_VOICES.map((voice) => (
                <div key={voice.id}>
                  {isCollapsed ? (
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="relative h-12 w-12 rounded-lg transition-all p-0 hover:scale-105 mx-auto overflow-hidden"
                          onClick={() => handleVoiceSelected(voice)}
                          disabled={isStartingInterview || !activeDomain}
                        >
                          {isStartingInterview ? (
                            <Loader2 className="h-5 w-5 animate-spin" />
                          ) : (
                            <Image
                              src={voice.avatar}
                              alt={voice.name}
                              width={48}
                              height={48}
                              className="w-full h-full object-cover rounded-lg"
                              onError={(e) => {
                                const target = e.target as HTMLImageElement;
                                target.src = '/placeholder-avatar.png';
                              }}
                            />
                          )}
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent side="right">
                        <p className="font-medium">{voice.name}</p>
                        <p className="text-xs text-muted-foreground">
                          {voice.description}
                        </p>
                      </TooltipContent>
                    </Tooltip>
                  ) : (
                    <Button
                      variant="ghost"
                      onClick={() => handleVoiceSelected(voice)}
                      disabled={isStartingInterview || !activeDomain}
                      className="w-full justify-start gap-3 h-12 px-2"
                    >
                      {isStartingInterview ? (
                        <Loader2 className="h-5 w-5 animate-spin" />
                      ) : (
                        <div className="relative w-8 h-8 rounded-full overflow-hidden shrink-0">
                          <Image
                            src={voice.avatar}
                            alt={voice.name}
                            fill
                            className="object-cover"
                            onError={(e) => {
                              const target = e.target as HTMLImageElement;
                              target.src = '/placeholder-avatar.png';
                            }}
                          />
                        </div>
                      )}
                      <div className="flex flex-col items-start">
                        <span className="font-medium text-sm">{voice.name}</span>
                        <span className="text-[10px] text-muted-foreground">
                          {voice.description}
                        </span>
                      </div>
                    </Button>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Sticky Bottom: Create Domain + Credits + Collapse Toggle */}
          <div className="sticky bottom-0 z-20 p-2 border-t bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/80 space-y-1">
            {/* Create Domain Button */}
            {isCollapsed ? (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-10 w-10 rounded-lg border-2 border-dashed border-muted-foreground/20 hover:border-muted-foreground/40 mx-auto"
                    onClick={() => setShowCreateModal(true)}
                  >
                    <Plus className="h-5 w-5 text-muted-foreground" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="right">Create Domain</TooltipContent>
              </Tooltip>
            ) : (
              <Button
                variant="ghost"
                onClick={() => setShowCreateModal(true)}
                className="w-full justify-start gap-3 h-10 border-2 border-dashed border-muted-foreground/20 hover:border-muted-foreground/40"
              >
                <Plus className="h-5 w-5 text-muted-foreground" />
                <span className="font-medium text-muted-foreground">
                  Create Domain
                </span>
              </Button>
            )}

            {/* Plan Badge + Credits */}
            {activeDomain && domainInfo && (
              <div
                className={cn(
                  'flex items-center gap-2',
                  isCollapsed ? 'flex-col' : 'justify-between px-2 py-1'
                )}
              >
                {/* Plan Badge */}
                {domainInfo.plan_type && domainInfo.plan_type !== 'free' && (
                  <div
                    className={cn(
                      'flex items-center gap-1.5 px-2 py-1 rounded-full cursor-pointer transition-all hover:scale-105',
                      domainInfo.plan_type === 'pro' &&
                        'bg-gradient-to-r from-apprendo-500 to-apprendo-600 shadow-lg shadow-apprendo-500/30',
                      domainInfo.plan_type === 'plus' &&
                        'bg-gradient-to-r from-blue-500 to-blue-600 shadow-lg shadow-blue-500/30'
                    )}
                    onClick={() => router.push('/dashboard/settings/billing')}
                  >
                    {domainInfo.plan_type === 'pro' ? (
                      <Crown className="h-3 w-3 text-white" />
                    ) : (
                      <Star className="h-3 w-3 text-white" />
                    )}
                    {!isCollapsed && (
                      <span className="text-xs font-semibold text-white uppercase">
                        {domainInfo.plan_type}
                      </span>
                    )}
                  </div>
                )}

                {/* Credits */}
                {isCollapsed ? (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => router.push('/dashboard/settings/billing')}
                        className="h-10 w-10 rounded-lg hover:bg-muted"
                      >
                        <div className="flex flex-col items-center justify-center gap-0.5">
                          <Sparkles className="h-4 w-4 text-muted-foreground" />
                          <span className="text-[10px] font-medium text-muted-foreground">
                            {domainInfo.credits}
                          </span>
                        </div>
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent side="right">
                      <div className="text-sm">
                        <span className="font-medium">{domainInfo.credits}</span>{' '}
                        credits remaining
                      </div>
                    </TooltipContent>
                  </Tooltip>
                ) : (
                  <Button
                    variant="ghost"
                    onClick={() => router.push('/dashboard/settings/billing')}
                    className="h-8 px-2 hover:bg-muted"
                  >
                    <Sparkles className="h-4 w-4 text-muted-foreground mr-1" />
                    <span className="text-sm font-medium text-muted-foreground">
                      {domainInfo.credits}
                    </span>
                  </Button>
                )}
              </div>
            )}

          </div>
        </div>
      </TooltipProvider>

      {/* Create Domain Modal */}
      <CreateDomainModal
        open={showCreateModal}
        onOpenChange={(open) => {
          setShowCreateModal(open);
          if (!open) {
            fetchDomains();
          }
        }}
      />

    </>
  );
}
